import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  EditorPresetsService,
  renderVersionedSchema,
} from '@principle-theorem/ng-interactions';
import {
  CurrentScopeFacade,
  OrganisationService,
  StateBasedNavigationService,
} from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  confirmationDialogData,
  DialogPresets,
  type IConfirmationDialogInput,
} from '@principle-theorem/ng-shared';
import {
  AutomatedFormIssue,
  AutomatedNotification,
  Automation,
  AutomationConfiguration,
  Brand,
  BrandLinks,
  Event,
  GeneratedTask,
  Patient,
  Practice,
  stafferToNamedDoc,
  TemplateDefinition,
} from '@principle-theorem/principle-core';
import {
  AnyAutomation,
  AnyAutomationConfiguration,
  AutomationContext,
  AutomationCreator,
  AutomationParentScope,
  AutomationStatus,
  IAutomatedFormIssueConfiguration,
  IAutomatedNotification,
  IAutomation,
  IAutomationCustomTemplate,
  IAutomationScopeData,
  IAutomationScopeRequirements,
  IBrand,
  IEvent,
  IGeneratedTaskConfiguration,
  IPractice,
  isAutomatedFormIssue,
  isAutomatedFormIssueConfiguration,
  isAutomatedNotification,
  isAutomatedNotificationConfiguration,
  isGeneratedTask,
  isGeneratedTaskConfiguration,
  TemplateScope,
  TimingDirection,
  type IAutomatedNotificationConfiguration,
  type IAutomationConfiguration,
} from '@principle-theorem/principle-core/interfaces';
import {
  addDoc,
  deleteDoc,
  DocumentReference,
  filterUndefined,
  Firestore,
  INamedTypeDocument,
  isWithRef,
  snapshot,
  snapshotDefined,
  toMoment,
  toTimestamp,
  undeleteDoc,
  type WithRef,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import {
  AutomatedNotificationConfigurationEditComponent,
  type IAutomatedNotificationConfigurationEditRequest,
  type IAutomatedNotificationConfigurationEditResponse,
} from '../components/automated-notification-configuration-edit/automated-notification-configuration-edit.component';
import {
  AutomationsByConfigDialogComponent,
  IAutomationsByConfigRequest,
} from '../components/automations-by-config-dialog/automations-by-config-dialog.component';
import {
  ITestAutomationConfigurationDialogRequest,
  ITestAutomationConfigurationDialogResponse,
  TestAutomationConfigurationDialogComponent,
} from '../components/test-automation-configuration-dialog/test-automation-configuration-dialog.component';
import {
  IUpdateExistingAutomationsRequest,
  UpdateExistingAutomationsDialogComponent,
} from '../components/update-existing-automations-dialog/update-existing-automations-dialog.component';
import { AutomationDialogService } from './automation-dialog.service';

@Injectable()
export class AutomationConfigurationService {
  private _dialog = inject(MatDialog);
  private _stateNav = inject(StateBasedNavigationService);
  private _basicDialog = inject(BasicDialogService);
  private _snackBar = inject(MatSnackBar);
  private _currentScope = inject(CurrentScopeFacade);
  private _automationDialog = inject(AutomationDialogService);
  private _organisation = inject(OrganisationService);
  private _editorPresets = inject(EditorPresetsService);

  async openEditDialog(
    request: IAutomatedNotificationConfigurationEditRequest
  ): Promise<IAutomatedNotificationConfigurationEditResponse | undefined> {
    return this._dialog
      .open<
        AutomatedNotificationConfigurationEditComponent,
        IAutomatedNotificationConfigurationEditRequest,
        IAutomatedNotificationConfigurationEditResponse
      >(
        AutomatedNotificationConfigurationEditComponent,
        DialogPresets.large({ height: '80%', data: request })
      )
      .afterClosed()
      .toPromise();
  }

  async editNotification(
    config: WithRef<IAutomatedNotificationConfiguration>,
    scope: TemplateScope
  ): Promise<void> {
    const response = await this.openEditDialog({
      config,
      scope,
    });
    if (!response) {
      return;
    }

    const brand = await snapshot(
      this._currentScope.currentBrand$.pipe(filterUndefined())
    );
    const templateRef =
      config.templateRef ??
      (await addDoc(
        Brand.templateCol(brand),
        TemplateDefinition.init({
          ...response.template,
          ownerScope: brand.ref,
        })
      ));
    const updatedAutomationConfig = {
      ...config,
      ...response.config,
      templateRef,
    };
    await Firestore.saveDoc(updatedAutomationConfig);

    const templateChanges = {
      ...response.template,
      automatedConfigurationRef: config.ref,
    };
    await Firestore.patchDoc(templateRef, templateChanges);
    this._snackBar.open('Notification configuration saved');

    await this.openUpdateExistingAutomations(updatedAutomationConfig);
  }

  async editTask(
    task: WithRef<IGeneratedTaskConfiguration>,
    context: AutomationContext
  ): Promise<void> {
    const updates = await this._automationDialog.openTaskDialog({
      task,
      useRelativeTime: true,
      context,
      configRef: task.ref,
    });
    if (!updates) {
      return;
    }

    const updatedTask = { ...task, ...updates };
    await Firestore.saveDoc(updatedTask);
    this._snackBar.open('Task configuration saved');

    await this.openUpdateExistingAutomations(updatedTask);
  }

  async editFormIssue(
    formIssue: WithRef<IAutomatedFormIssueConfiguration>
  ): Promise<void> {
    const updates = await this._automationDialog.openAutomatedFormIssueDialog({
      formIssue,
      useRelativeTime: true,
    });
    if (!updates) {
      return;
    }

    const updatedFormIssue = {
      ...formIssue,
      ...updates,
    };
    await Firestore.saveDoc(updatedFormIssue);
    this._snackBar.open('Generate Form configuration saved');

    await this.openUpdateExistingAutomations(updatedFormIssue);
  }

  async editConfig(
    automation: WithRef<IAutomationConfiguration>,
    context: AutomationContext,
    scope: TemplateScope
  ): Promise<void> {
    if (
      isAutomatedNotificationConfiguration(automation) &&
      isWithRef<IAutomatedNotificationConfiguration>(automation)
    ) {
      return this.editNotification(automation, scope);
    }
    if (
      isGeneratedTaskConfiguration(automation) &&
      isWithRef<IGeneratedTaskConfiguration>(automation)
    ) {
      return this.editTask(automation, context);
    }
    if (
      isAutomatedFormIssueConfiguration(automation) &&
      isWithRef<IAutomatedFormIssueConfiguration>(automation)
    ) {
      return this.editFormIssue(automation);
    }

    throw new Error('Unsupported automation configuration type');
  }

  async deleteConfig(
    automation: WithRef<IAutomationConfiguration>
  ): Promise<void> {
    const confirmed = await this._basicDialog.confirm(
      confirmationDialogData({
        title: 'Delete Automation Configuration',
        prompt: [
          'Are you sure you want to delete this automation configuration?',
          'Automations using this configuration will no longer run when scheduled.',
        ],
        submitLabel: 'Delete',
        submitColor: 'warn',
      })
    );
    if (!confirmed) {
      return;
    }
    await deleteDoc(automation.ref, undefined, undefined, {
      isActive: false,
    });
    if (isAutomatedNotificationConfiguration(automation)) {
      await deleteDoc(automation.templateRef);
    }
    this._snackBar.open('Deleted Automation Configuration');
  }

  async restoreConfig(
    automation: WithRef<IAutomationConfiguration>
  ): Promise<void> {
    const confirmed = await this._basicDialog.confirm(
      confirmationDialogData({
        title: 'Restore Automation Configuration',
        prompt: [
          'Are you sure you want to restore this automation configuration?',
          'Once restored this configuration will remain disabled until you enable it.',
          'Automations using this configuration will not run until you enable it.',
        ],
        submitLabel: 'Restore',
      })
    );

    if (!confirmed) {
      return;
    }
    await undeleteDoc(automation.ref);
    if (isAutomatedNotificationConfiguration(automation)) {
      await undeleteDoc(automation.templateRef);
    }
    this._snackBar.open('Restored Automation Configuration');
  }

  async setActive(
    automation: WithRef<IAutomationConfiguration>,
    isActive: boolean
  ): Promise<void> {
    const enableDialogData: IConfirmationDialogInput = {
      title: 'Enable Automation Configuration',
      prompt: [
        'Are you sure you want to enable this automation configuration?',
        'The automation will be added to all relevant future appointments.',
      ],
      submitLabel: 'Enable',
    };

    const disableDialogData: IConfirmationDialogInput = {
      title: 'Disable Automation Configuration',
      prompt: [
        'Are you sure you want to disable this automation configuration?',
        'Automations using this configuration will no longer run when scheduled.',
      ],
      submitLabel: 'Disable',
    };

    const confirmed = await this._basicDialog.confirm(
      confirmationDialogData(isActive ? enableDialogData : disableDialogData)
    );
    if (!confirmed) {
      return;
    }
    await Firestore.patchDoc(automation.ref, { isActive });

    const actionLabel = isActive ? 'Enabled' : 'Disabled';
    this._snackBar.open(`${actionLabel} Automation Configuration`);
  }

  async openAutomations(
    config: WithRef<IAutomationConfiguration>
  ): Promise<void> {
    const data: IAutomationsByConfigRequest = { config };
    await this._dialog
      .open<
        AutomationsByConfigDialogComponent,
        IAutomationsByConfigRequest,
        void
      >(
        AutomationsByConfigDialogComponent,
        DialogPresets.large({ height: '80%', data })
      )
      .afterClosed()
      .toPromise();
  }

  canTestAutomationConfig(config: AnyAutomationConfiguration): boolean {
    return (
      isAutomatedNotificationConfiguration(config) ||
      isGeneratedTaskConfiguration(config)
    );
  }

  async testAutomationConfig(
    config: AnyAutomationConfiguration,
    scope: TemplateScope,
    context: AutomationContext
  ): Promise<void> {
    const brand = await snapshotDefined(this._organisation.brand$);
    const practice = await snapshot(this._currentScope.currentPractice$);

    const request: ITestAutomationConfigurationDialogRequest = {
      brand,
      practice,
      scope,
      config,
      context,
    };
    const response = await this._dialog
      .open<
        TestAutomationConfigurationDialogComponent,
        ITestAutomationConfigurationDialogRequest,
        ITestAutomationConfigurationDialogResponse
      >(
        TestAutomationConfigurationDialogComponent,
        DialogPresets.medium({ data: request })
      )
      .afterClosed()
      .toPromise();
    if (!response) {
      return;
    }

    const scopeData = Automation.buildScopeData(scope, response.scopeRef);
    const appointment = response.appointmentRef
      ? await Firestore.getDoc(response.appointmentRef)
      : undefined;

    const automation = await this._buildTestAutomation(
      config,
      response.practice,
      scopeData,
      appointment?.event
    );

    if (!automation) {
      this._snackBar.open('Could not build test automation.');
      return;
    }

    await Firestore.addDoc(Patient.automationCol(response.patient), automation);

    const thirtySeconds = 30 * 1000;
    const snackBarRef = this._snackBar.open(
      `Automation added to ${response.patient.name}.`,
      'Open Patient Automations',
      { duration: thirtySeconds }
    );
    snackBarRef.onAction().subscribe(() => {
      const link = BrandLinks.patient(response.patient.ref);
      if (link) {
        void this._stateNav.brand([...link.segments, 'automations']);
      }
    });
  }

  async openUpdateExistingAutomations(
    config: WithRef<IAutomationConfiguration>
  ): Promise<void> {
    const haveNotRunStatuses = [
      AutomationStatus.Pending,
      AutomationStatus.Queued,
      AutomationStatus.Cancelled,
      AutomationStatus.Disabled,
      AutomationStatus.Skipped,
    ];

    const automationCount = await AutomationConfiguration.getAutomationCount(
      config.ref,
      toTimestamp(),
      undefined,
      haveNotRunStatuses
    );

    if (automationCount <= 0) {
      return;
    }

    await this._dialog
      .open<
        UpdateExistingAutomationsDialogComponent,
        IUpdateExistingAutomationsRequest,
        void
      >(
        UpdateExistingAutomationsDialogComponent,
        DialogPresets.small({
          data: { config, automationCount },
        })
      )
      .afterClosed()
      .toPromise();
  }

  private async _buildTestAutomation(
    config: AnyAutomationConfiguration,
    practice: WithRef<IPractice>,
    scopeData: IAutomationScopeData,
    appointmentEvent?: IEvent
  ): Promise<IAutomation<AnyAutomation> | undefined> {
    const brandRef = Practice.brandRef(practice);
    const staffer = await snapshotDefined(this._organisation.staffer$);
    const creator = {
      ...stafferToNamedDoc(staffer),
      type: AutomationCreator.Staffer,
    };
    const scopeRequirements: IAutomationScopeRequirements = {
      parentScope: AutomationParentScope.Patient,
      ...scopeData,
    };

    const now = moment();
    const defaultFrom =
      config.timing.direction === TimingDirection.After
        ? now.clone().subtract(2, 'hour')
        : now.clone().add(1, 'hour');
    const defaultTo = defaultFrom.clone().add(1, 'hour');

    const event =
      appointmentEvent ??
      Event.init({
        practice,
        from: toTimestamp(defaultFrom),
        to: toTimestamp(defaultTo),
      });

    const automation = await this._generateAutomationForConfig(
      config,
      creator,
      event,
      brandRef,
      scopeRequirements
    );
    if (!automation) {
      return;
    }

    const triggerDate = Automation.applyGracePeriod(
      automation.triggerDate ? toMoment(automation.triggerDate) : moment(),
      practice.settings.timezone
    );
    automation.triggerDate = toTimestamp(triggerDate);
    automation.triggerAfterDate = toTimestamp(triggerDate);

    automation.configRef = undefined; // Clear configRef so that test automations can run even if config is disabled

    return automation;
  }

  private async _generateAutomationForConfig(
    config: AnyAutomationConfiguration | WithRef<AnyAutomationConfiguration>,
    creator: INamedTypeDocument<object, AutomationCreator>,
    event: IEvent,
    brandRef: DocumentReference<IBrand>,
    scopeData: IAutomationScopeRequirements
  ): Promise<IAutomation<AnyAutomation> | undefined> {
    if (isAutomatedNotification(config)) {
      config.customTemplate = await this._resolveCustomTemplate(config);
      return AutomatedNotification.toAutomation(
        config,
        creator,
        event,
        brandRef,
        scopeData
      );
    }

    if (isGeneratedTask(config)) {
      return GeneratedTask.toAutomation(
        config,
        creator,
        event,
        brandRef,
        scopeData
      );
    }

    if (isAutomatedFormIssue(config)) {
      // TODO: Known bug, automation gets stuck in pending. parentScope appears the be the issue.
      return AutomatedFormIssue.toAutomation(config, creator, event, brandRef);
    }
  }

  private async _resolveCustomTemplate(
    config: IAutomatedNotification
  ): Promise<IAutomationCustomTemplate | undefined> {
    if (!config.customTemplate) {
      if (isAutomatedNotificationConfiguration(config)) {
        const template = await Firestore.getDoc(config.templateRef);
        return {
          content: template.content,
          scope: template.scope,
          renderedTemplate: renderVersionedSchema(
            template.content,
            this._editorPresets.defaultToHTMLExtensions()
          ),
        };
      }
      return;
    }

    return {
      content: config.customTemplate.content,
      scope: config.customTemplate.scope,
      renderedTemplate: renderVersionedSchema(
        config.customTemplate.content,
        this._editorPresets.defaultToHTMLExtensions()
      ),
    };
  }
}
